import React, { useContext, useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { deleteTrackingData, getTrackingData, getTrackingDataExport, getUser } from "../services/adminServices";
import { AppContext } from "../context/AppContext";
import { toast } from "react-toastify"
import moment from "moment";
import { CONFIG } from "../config/general";
import { checkPermission } from "../helper/helper";
import { DayWiseTimeTracker } from "./DayWiseTimeTracker";
import DropdownWithCheckbox from "./Selection";

const TimeTracker = () => {
  const { login } = useContext(AppContext);
  const [filters, setFilters] = useState({
    selectedUsers: [],
    startTime: new Date(),
    endTime: new Date(),
    billable: "all",
    title: "",
    page: 1,
    limit: 100,
    key: 'time',
    direction: 'desc'
  });
  const [pagination, setPagination] = useState({})
  const [selectedIds, setSelectedIds] = useState([])
  const [users, setUsers] = useState([])
  const [totalDuration, setTotalDuration] = useState(0)
  const [totalBillableDuration, setTotalBillableDuration] = useState(0)
  const [conformationModal, setConformationModal] = useState(false)
  const [loading, setLoading] = useState(false)
  const [activeTab, setActiveTab] = useState('daywise');

  const [entries, setEntries] = useState([]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleSort = (key) => {
    setFilters((prev) => {
      const direction = prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc';
      return { ...prev, key, direction };
    });
  };

  const handleUserSelection = (user) => {
    setFilters((prev) => {
      const selectedUsers = prev.selectedUsers.includes(user)
        ? prev.selectedUsers.filter((u) => u !== user)
        : [...prev.selectedUsers, user];
      return { ...prev, selectedUsers };
    });
  };

  const handleNext = () => {
    if (pagination.currentPage < pagination.totalPages) {
      const newPage = pagination.currentPage + 1;
      setFilters({ ...filters, page: newPage });
      getTracking({ ...filters, page: newPage })
    }
  };

  const handlePrev = () => {
    if (pagination.currentPage > 1) {
      const newPage = pagination.currentPage - 1;
      setFilters({ ...filters, page: newPage });
      getTracking({ ...filters, page: newPage })
    }
  };

  const getAllUsers = async () => {
    try {
      let result = await getUser(login.token, { name: "" });
      setUsers(result.data);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  function formatDate(date, isStart) {
    const localDate = new Date(date);
    if (isStart) {
      localDate.setHours(0, 0, 0, 0);
    } else {
      localDate.setHours(23, 59, 59, 999);
    }
    const utcDate = new Date(localDate.toISOString());
    return utcDate;
  }

  const getTracking = async (filter = filters) => {
    try {
      setLoading(true)
      let result = await getTrackingData(login.token, { ...filter, startTime: formatDate(filter.startTime, true), endTime: formatDate(filter.endTime, false) });
      setEntries(result.data);
      setPagination(result.pagination)
      setTotalDuration(result.totalDuration)
      setTotalBillableDuration(result.billableDuration)
      setLoading(false)
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const getTrackingExport = async (filter = filters) => {
    try {
      setLoading(true);

      const payload = {
        ...filter,
        startTime: formatDate(filter.startTime, true),
        endTime: formatDate(filter.endTime, false),
      };

      await getTrackingDataExport(login.token, payload);

      toast.success("Export started. You will receive a notification when the file is ready.");
      setLoading(false);
    } catch (error) {
      console.error("Export request error:", error);
      toast.error("Failed to start export.");
      setLoading(false);
    }
  };


  const formatDuration = (totalSeconds) => {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(
      2,
      "0"
    )}:${String(seconds).padStart(2, "0")}`;
  };

  const deleteTime = async (Ids = selectedIds) => {
    try {
      if (Ids.length == 0) { return }
      let result = await deleteTrackingData(login.token, { "tracking": Ids });
      if (result.success) {
        toast.success(result.message)
        getTracking()
        setConformationModal(false)
        setSelectedIds([])
      } else {
        toast.error(result.message)
      }
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  }

  const handleCheckboxChange = (id, isChecked) => {
    setSelectedIds((prev) =>
      isChecked ? [...prev, id] : prev.filter((selectedId) => selectedId !== id)
    );
  };

  useEffect(() => {
    getAllUsers();
    getTracking();
  }, [])

  useEffect(() => {
    setFilters({ ...filters, page: 0 })
    getTracking({ ...filters, page: 0 });
  }, [filters.limit, filters.direction, filters.key])

  const keyRules = {
    // Mouse
    "MOUSE": "#C678DD",
    "MOUSE LEFT": "#C678DD",
    "MOUSE RIGHT": "#C678DD",
    "MOUSE MIDDLE": "#C678DD",

    // Arrows
    "ARROW": "#61AFEF",
    "UP ARROW": "#61AFEF",
    "DOWN ARROW": "#61AFEF",
    "LEFT ARROW": "#61AFEF",
    "RIGHT ARROW": "#61AFEF",

    // Modifiers
    "CTRL": "#E06C75",
    "SHIFT": "#E06C75",
    "LEFT SHIFT": "#E06C75",
    "RIGHT SHIFT": "#E06C75",
    "ALT": "#E06C75",
    "LEFT ALT": "#E06C75",
    "RIGHT ALT": "#E06C75",
    "META": "#E06C75",
    "LEFT META": "#E06C75",
    "RIGHT META": "#E06C75",

    // Numpad
    "NUMPAD": "#ABB2BF",
    "NUMPAD PLUS": "#ABB2BF",
    "NUMPAD MINUS": "#ABB2BF",
    "NUMPAD MULTIPLY": "#ABB2BF",
    "NUMPAD DIVIDE": "#ABB2BF",
    "NUMPAD ENTER": "#ABB2BF",

    // Function keys
    "ENTER": "#98C379",
    "RETURN": "#98C379",
    "BACKSPACE": "#BE5046",
    "TAB": "#D19A66",
    "ESC": "#E06C75",
    "DELETE": "#BE5046",
    "INSERT": "#E5C07B",
    "HOME": "#E5C07B",
    "END": "#E5C07B",
    "PAGE UP": "#E5C07B",
    "PAGE DOWN": "#E5C07B",

    // Other keys
    "SPACE": "#56B6C2",
    "CAPS LOCK": "#E06C75",
    "NUM LOCK": "#E06C75",
    "SCROLL LOCK": "#E06C75"
  };



  function colorizeKeys(text) {
    return text
      .split(",")
      .map(part => {
        const original = part.trim();
        const cleaned = original.replace(/\(\d+\)/g, "").trim();
        const upper = cleaned.toUpperCase();

        // Skip single letters / numbers
        if (/^[A-Z0-9]$/.test(cleaned)) return original;

        // Sort rules by longest first (avoid ARROW matching after UP ARROW)
        const sortedRules = Object.keys(keyRules).sort((a, b) => b.length - a.length);

        for (const rule of sortedRules) {
          const ruleUpper = rule.toUpperCase();

          // PARTIAL MATCH LOGIC
          if (upper.includes(ruleUpper)) {
            return `<span style="color:${keyRules[rule]};">${original}</span>`;
          }
        }

        return original;
      })
      .join(", ");
  }




  return (
    <>
      <div className="details-table mt-3">
        <ul className="nav nav-tabs">
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === 'daywise' ? 'active' : ''}`}
              onClick={() => setActiveTab('daywise')}
            >
              Day Wise
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === 'custom' ? 'active' : ''}`}
              onClick={() => setActiveTab('custom')}
            >
              Custom Time Tracker
            </button>
          </li>
        </ul>

        <div className="tab-content mt-3">
          {activeTab === 'daywise' && (
            <div className="tab-pane active">
              <DayWiseTimeTracker />
            </div>
          )}

          {activeTab === 'custom' && (
            <div className="tab-pane active">
              <div className="filters mb-2 p-2 border rounded" style={{ backgroundColor: "var(--card-background)" }}>
                <div className="row align-items-baseline">
                  {/* User Filter */}
                  <div className="col-md-3 mb-3">
                    <label className="form-label fw-bold">Name:</label>

                    {/* ✅ Reusable dropdown with checkboxes */}
                    <DropdownWithCheckbox
                      options={users} // expects [{ _id, name, label? }]
                      selectedItems={filters.selectedUsers}
                      setSelectedItems={(newSelected) =>
                        setFilters({ ...filters, selectedUsers: newSelected })
                      }
                    />

                    {/* ✅ Show selected user names */}
                    <div className="mt-2">
                      <small>
                        {filters.selectedUsers?.length > 0
                          ? `Selected: ${filters.selectedUsers
                            .map((id) => users.find((u) => u._id === id)?.name)
                            .join(", ")}`
                          : "No users selected"}
                      </small>
                    </div>
                  </div>


                  {/* Date Filter */}
                  <div className="col-md-3 mb-3 align-content-center">
                    <label className="form-label fw-bold">Date Range:</label>
                    <DatePicker
                      selectsRange={true}
                      startDate={filters.startTime}
                      endDate={filters.endTime}
                      onChange={(update) => setFilters({ ...filters, startTime: update[0], endTime: update[1] })}
                      className="form-select"
                      calendarStartDay={1}
                      maxDate={new Date()}
                      placeholderText="Select date range"
                    />
                  </div>

                  {/* Billable Filter */}
                  <div className="col-md-3 mb-3">
                    <label className="form-label fw-bold">Billing:</label>
                    <div className="dropdown">
                      <button
                        className="btn btn-sm btn-outline-secondary dropdown-toggle w-100"
                        type="button"
                        id="billingDropdown"
                        data-bs-toggle="dropdown"
                        aria-expanded="false"
                      >
                        {filters.billable === "all"
                          ? "All"
                          : filters.billable === "true"
                            ? "Billable"
                            : "Non-billable"}
                      </button>

                      <ul className="dropdown-menu w-100" aria-labelledby="billingDropdown">
                        <li>
                          <button
                            className="dropdown-item"
                            type="button"
                            onClick={() => handleFilterChange({ target: { name: "billable", value: "all" } })}
                          >
                            All
                          </button>
                        </li>
                        <li>
                          <button
                            className="dropdown-item"
                            type="button"
                            onClick={() => handleFilterChange({ target: { name: "billable", value: "true" } })}
                          >
                            Billable
                          </button>
                        </li>
                        <li>
                          <button
                            className="dropdown-item"
                            type="button"
                            onClick={() => handleFilterChange({ target: { name: "billable", value: "false" } })}
                          >
                            Non-billable
                          </button>
                        </li>
                      </ul>
                    </div>
                  </div>


                  {/* Description Search */}
                  <div className="col-md-3 mb-3">
                    <label className="form-label fw-bold">Search Description:</label>
                    <input
                      type="text"
                      name="title"
                      value={filters.title}
                      className="form-control"
                      onChange={handleFilterChange}
                      placeholder="Enter description"
                    />
                  </div>
                </div>
                <div className="d-flex justify-content-between">
                  <div className="d-flex justify-content-end">
                    <h5>Total Time:{formatDuration(totalDuration)}</h5>
                  </div>
                  <div className="d-flex justify-content-end">
                    <h5>Total Billable Time:{formatDuration(totalBillableDuration)}</h5>
                  </div>
                  <button className="btn btn-success" onClick={() => { getTracking({ ...filters, page: 0 }) }} disabled={loading}>Apply Filters</button>
                  <button className="btn btn-success" onClick={() => { getTrackingExport({ ...filters, page: 0 }) }} disabled={loading}>Export Tracking</button>
                </div>
              </div>
              {!loading ? <div className="table-responsive">
                <div className="custom-pagination">
                  <div className="d-flex gap-2 align-items-center">
                    <span className="showing-entries">Showing {(pagination.currentPage - 1) * filters.limit + 1} - {Math.min((pagination.currentPage) * filters.limit, pagination.totalDocuments)} of {pagination.totalDocuments}</span>
                    <div className="dropdown">
                      <div>
                        <div className="dropdown">
                          <button
                            className="btn btn-sm btn-outline-secondary dropdown-toggle w-100"
                            type="button"
                            id="limitDropdown"
                            data-bs-toggle="dropdown"
                            aria-expanded="false"
                          >
                            {filters.limit || "Select Limit"}
                          </button>

                          <ul className="dropdown-menu w-100" aria-labelledby="limitDropdown">
                            {[50, 100, 150, 200].map((val) => (
                              <li key={val}>
                                <button
                                  className="dropdown-item"
                                  type="button"
                                  onClick={() => setFilters({ ...filters, limit: val })}
                                >
                                  {val}
                                </button>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>

                    </div>
                    {selectedIds.length > 0 && (
                      <div>
                        <button className="btn btn-danger" onClick={() => setConformationModal(true)}>Delete</button>
                      </div>
                    )}
                  </div>
                  <div className="page-controls">
                    <button
                      className="page-btn prev"
                      onClick={handlePrev}
                      disabled={pagination.currentPage === 1} // Disable button on first page
                    >
                      <i className="fas fa-chevron-left"></i>
                    </button>
                    <span className="current-page">{pagination.currentPage}</span>
                    <span className="separator">/</span>
                    <span className="total-pages">{pagination.totalPages}</span>
                    <button
                      className="page-btn next"
                      onClick={handleNext}
                      disabled={pagination.currentPage === pagination.totalPages} // Disable button on last page
                    >
                      <i className="fas fa-chevron-right"></i>
                    </button>
                  </div>
                </div>
                <table className="table-hover table-bordered align-middle">
                  <thead>
                    <tr>
                      {checkPermission(login.user, "timetracking:write") && (
                        <th
                          style={{ cursor: "pointer" }}
                          onClick={() => {
                            selectedIds.length == entries.length
                              ? setSelectedIds([])
                              : setSelectedIds(entries.map((x) => x._id));
                          }}
                        >
                          All
                        </th>
                      )}
                      <th>No</th>
                      <th
                        style={{ wordBreak: "break-all", cursor: "pointer" }}
                        onClick={() => handleSort("title")}
                      >
                        Description
                        {filters.key === "title" &&
                          (filters.direction === "asc" ? " ▲" : " ▼")}
                      </th>
                      <th
                        style={{ wordBreak: "break-word", cursor: "pointer" }}
                        onClick={() => handleSort("name")}
                      >
                        Name
                        {filters.key === "name" &&
                          (filters.direction === "asc" ? " ▲" : " ▼")}
                      </th>
                      <th
                        style={{ cursor: "pointer", minWidth: "100px" }}
                        onClick={() => handleSort("time")}
                      >
                        Time
                        {filters.key === "time" &&
                          (filters.direction === "asc" ? " ▲" : " ▼")}
                      </th>
                      <th
                        style={{ cursor: "pointer" }}
                        onClick={() => handleSort("duration")}
                      >
                        Duration
                        {filters.key === "duration" &&
                          (filters.direction === "asc" ? " ▲" : " ▼")}
                      </th>
                      <th>Billable</th>
                      <th>Screenshots</th>
                    </tr>
                  </thead>

                  <tbody>
                    {entries.map((entry, i) => {
                      console.log(entry)
                      const duration = entry.endTime
                        ? moment(entry.endTime).diff(moment(entry.startTime), "seconds")
                        : moment().diff(moment(entry.startTime), "seconds");

                      const startTimeFormatted = entry.startTime
                        ? {
                          time: moment(entry.startTime).format("HH:mm:ss"),
                          date: moment(entry.startTime).format("DD-MM-YY"),
                        }
                        : { time: "N/A", date: "N/A" };

                      const endTimeFormatted = entry.endTime
                        ? {
                          time: moment(entry.endTime).format("HH:mm:ss"),
                          date: moment(entry.endTime).format("DD-MM-YY"),
                        }
                        : { time: "Still Active", date: "" };

                      // ✅ Generate tooltip content from keyLogs
                      const tooltipText =
                        entry.keyLogs && entry.keyLogs.length > 0
                          ? entry.keyLogs
                            .map((log) => {
                              const time = moment(log.time).format("HH:mm:ss");
                              const keys = log.keys
                              return `${time} → ${keys}`;
                            })
                            .join("\n")
                          : "No key logs found";

                      return (
                        <tr
                          key={entry._id}
                          data-bs-toggle="tooltip"
                          data-bs-placement="top"
                          data-bs-html="true"
                          title={tooltipText} // show multiline tooltip
                          style={{ cursor: "pointer" }}
                        >
                          {checkPermission(login.user, "timetracking:write") && (
                            <td>
                              <div
                                className="form-check d-flex justify-content-center"
                                style={{ fontSize: "20px" }}
                              >
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  id={`checkbox-${entry._id}`}
                                  checked={selectedIds.includes(entry._id)}
                                  onChange={(e) =>
                                    handleCheckboxChange(entry._id, e.target.checked)
                                  }
                                />
                                <label
                                  className="form-check-label"
                                  htmlFor={`checkbox-${entry._id}`}
                                ></label>
                              </div>
                            </td>
                          )}

                          <td>{(pagination.currentPage - 1) * filters.limit + (i + 1)}</td>
                          <td style={{ wordBreak: "break-all" }}>
                            {entry.title} |- {entry?.owner?.name}

                            {entry?.keyLogs?.length > 0 ? (
                              <div>
                                {entry.keyLogs.map(x => (
                                  <p
                                    key={x.time}
                                    dangerouslySetInnerHTML={{
                                      __html: `${moment(x.time).format("HH:mm:ss")} :- ${colorizeKeys(x.keys)}`
                                    }}
                                  />
                                ))}
                              </div>
                            ) : (
                              <p>No Logs Found</p>
                            )}

                            <>
                              {entry?.warnings?.map(x => (
                                <p key={x.warningMessage}>{x.warningMessage}</p>
                              ))}
                            </>
                          </td>

                          <td>{entry.user?.name}</td>
                          <td style={{ justifyItems: "center" }}>
                            <div style={{ width: "max-content" }}>
                              {startTimeFormatted.time} {" : "} {endTimeFormatted.time}
                            </div>
                            <div style={{ placeSelf: "center" }}>
                              {startTimeFormatted.date}
                            </div>
                          </td>
                          <td>
                            {duration > 0 ? formatDuration(parseInt(duration)) : duration}
                          </td>
                          <td>
                            <span
                              className={`badge ${entry.billable ? "bg-success" : "bg-danger"
                                }`}
                            >
                              {entry.billable ? "Yes" : "No"}
                            </span>
                          </td>
                          <td>
                            {entry.screenshots?.length > 0 ? (
                              <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                                {entry.screenshots.map((ss, index) => (
                                  <img
                                    key={index}
                                    src={`${CONFIG.REACT_APP_UPLOADS}/screenshots/${entry.user?.username}/${ss.filename}`}
                                    alt="Screenshot"
                                    style={{
                                      width: "70px",
                                      height: "50px",
                                      objectFit: "cover",
                                      borderRadius: "4px",
                                      cursor: "pointer",
                                      border: "1px solid #ccc",
                                    }}
                                    onClick={() => window.open(`${CONFIG.REACT_APP_UPLOADS}/screenshots/${entry.user?.username}/${ss.filename}`, "_blank")}
                                  />
                                ))}
                              </div>
                            ) : (
                              <span className="text-muted">No Screenshots</span>
                            )}
                          </td>

                        </tr>
                      );
                    })}

                    {entries.length === 0 && (
                      <tr>
                        <td colSpan="7" className="text-center text-muted">
                          No entries found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>

                {/* Modal */}
                <div className={`modal fade ${conformationModal ? "show d-block" : ""}`} tabIndex="-1" role="dialog">
                  <div className="modal-dialog">
                    <div className="modal-content">
                      <div className="modal-header">
                        <h5 className="modal-title">Confirm Action</h5>
                        <button type="button" className="btn-close" onClick={() => setConformationModal(false)}></button>
                      </div>
                      <div className="modal-body">Are you sure you want to delete this item?</div>
                      <div className="modal-footer">
                        <button type="button" className="btn btn-secondary" onClick={() => setConformationModal(false)}>Cancel</button>
                        <button type="button" className="btn btn-danger" onClick={() => { deleteTime() }}>Delete</button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Backdrop (for Bootstrap modal behavior) */}
                {conformationModal && <div className="modal-backdrop fade show" onClick={() => setConformationModal(false)}></div>}
              </div> : <>
                <div className="heartbeat-loader">
                  <img
                    src={CONFIG.REACT_APP_UPLOADS + "/Icons/logo.png"}
                    alt="Heartbeat Loader"
                    className="heartbeat-image"
                    style={{ width: "100px" }}
                  />
                </div>
              </>}

            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default TimeTracker;
